import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ImagePlus, X, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { quoteTypes } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nom: "",
  prenom: "",
  telephone: "",
  email: "",
  adresse: "",
  type_prestation: "",
  description: "",
  privacy_acceptee: false,
};

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-semibold text-forest mb-1.5">
    {children}
  </label>
);

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 6);
    setFiles(list.map((f) => ({ file: f, url: URL.createObjectURL(f) })));
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_acceptee) {
      toast.error("Veuillez accepter la politique de confidentialité.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, photos: files.map((f) => f.file.name) });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          ? "Certains champs sont invalides, merci de vérifier votre saisie."
          : "Une erreur est survenue. Merci de réessayer."
      );
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        data-testid="quote-form-success"
        className="bg-white rounded-3xl border border-forest/10 shadow-xl shadow-forest/5 p-10 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
          className="mx-auto w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center"
        >
          <CheckCircle2 size={34} className="text-moss" />
        </motion.div>
        <h3 className="mt-6 font-serif text-3xl text-forest">Merci {form.prenom} !</h3>
        <p className="mt-3 text-coal-muted leading-relaxed max-w-md mx-auto">
          Votre demande de devis a bien été envoyée. Notre équipe revient vers vous rapidement avec une proposition claire et adaptée à votre projet.
        </p>
        <button
          onClick={() => { setDone(false); setForm(initial); setFiles([]); }}
          className="btn-primary mt-8"
          data-testid="quote-form-reset-button"
        >
          Faire une nouvelle demande
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="bg-white rounded-3xl border border-forest/10 shadow-xl shadow-forest/5 p-6 sm:p-9 space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="q-nom">Nom *</Label>
          <input id="q-nom" required data-testid="quote-form-input-nom" className="field" value={form.nom} onChange={set("nom")} placeholder="Votre nom" />
        </div>
        <div>
          <Label htmlFor="q-prenom">Prénom *</Label>
          <input id="q-prenom" required data-testid="quote-form-input-prenom" className="field" value={form.prenom} onChange={set("prenom")} placeholder="Votre prénom" />
        </div>
        <div>
          <Label htmlFor="q-tel">Téléphone *</Label>
          <input id="q-tel" required type="tel" data-testid="quote-form-input-telephone" className="field" value={form.telephone} onChange={set("telephone")} placeholder="0470 12 34 56" />
        </div>
        <div>
          <Label htmlFor="q-email">E-mail *</Label>
          <input id="q-email" required type="email" data-testid="quote-form-input-email" className="field" value={form.email} onChange={set("email")} placeholder="vous@exemple.be" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="q-adresse">Adresse / commune *</Label>
          <input id="q-adresse" required data-testid="quote-form-input-adresse" className="field" value={form.adresse} onChange={set("adresse")} placeholder="Ex. : votre commune ou quartier" />
        </div>
        <div>
          <Label htmlFor="q-type">Type de prestation *</Label>
          <select id="q-type" required data-testid="quote-form-input-prestation" className="field" value={form.type_prestation} onChange={set("type_prestation")}>
            <option value="" disabled>Choisir une prestation…</option>
            {quoteTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor="q-desc">Description du projet *</Label>
        <textarea id="q-desc" required rows={5} data-testid="quote-form-input-description" className="field resize-none" value={form.description} onChange={set("description")} placeholder="Décrivez votre besoin : surface, état actuel, souhait, fréquence d'entretien…" />
      </div>

      <div>
        <Label htmlFor="q-photos">Ajouter des photos (optionnel)</Label>
        <label
          htmlFor="q-photos"
          data-testid="quote-form-photos-dropzone"
          className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-forest/20 bg-sand-mid/60 px-6 py-7 text-center cursor-pointer hover:border-moss/50 hover:bg-sand-mid transition-colors"
        >
          <ImagePlus size={22} className="text-moss" />
          <span className="text-sm text-coal-muted">Cliquez pour ajouter des photos de votre extérieur</span>
          <span className="text-[11px] text-coal/40">Jusqu'à 6 photos — JPG, PNG</span>
          <input id="q-photos" type="file" accept="image/*" multiple className="hidden" data-testid="quote-form-input-photos" onChange={onFiles} />
        </label>
        {files.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2.5" data-testid="quote-form-photos-previews">
            {files.map((f, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-forest/10 group">
                <img src={f.url} alt={f.file.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  aria-label={`Retirer ${f.file.name}`}
                  className="absolute top-1 right-1 p-1 rounded-full bg-coal/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="quote-form-privacy-wrapper">
        <input type="checkbox" checked={form.privacy_acceptee} onChange={set("privacy_acceptee")} className="mt-0.5 w-4 h-4 accent-[#1E3A2B]" data-testid="quote-form-input-privacy" />
        <span className="text-[13px] leading-relaxed text-coal-muted">
          J'accepte que mes informations soient utilisées pour traiter ma demande de devis, conformément à la{" "}
          <a href="/politique-de-confidentialite" className="text-moss font-semibold underline underline-offset-2">politique de confidentialité</a>. *
        </span>
      </label>

      <button type="submit" disabled={sending} data-testid="quote-form-submit" className="btn-primary w-full text-base disabled:opacity-60 disabled:pointer-events-none">
        {sending ? (
          <>
            <Loader2 size={17} className="animate-spin" /> Envoi en cours…
          </>
        ) : (
          <>
            Demander mon devis <ArrowRight size={16} />
          </>
        )}
      </button>
      <p className="flex items-center justify-center gap-1.5 text-[11px] text-coal/45">
        <ShieldCheck size={13} className="text-moss" /> Réponse rapide — devis gratuit et sans engagement
      </p>
    </form>
  );
}
